/**
 * Composição do bento.
 *
 * O bento é montado por LINHAS, não por um padrão de itens que se repete: cada
 * linha é escolhida de um ciclo e sempre fecha as 3 colunas, então nunca sobra
 * buraco, qualquer que seja a quantidade.
 *
 * Vocabulário, sobre TRÊS colunas:
 *   major = 2 colunas (2/3 da largura)   minor = 1 coluna (1/3)
 *
 * Três colunas — e não duas iguais — é o que faz o bento parecer bento: com
 * duas colunas do mesmo tamanho o resultado fica indistinguível da grade
 * regular. O contraste vem de células de larguras DIFERENTES na mesma linha.
 *
 * Todas as linhas têm a mesma altura (o `major` fica panorâmico, o `minor`
 * quase quadrado). Não há span de 2 linhas de propósito: num grid de altura
 * automática o `1fr` resolve para o conteúdo, e um card de 2 linhas exigiria
 * altura de linha fixa — foi o que colapsou as linhas numa tentativa anterior.
 */

export type BentoSpan = "major" | "minor" | "full";

/**
 * As formas de fechar uma linha de 3 colunas. `full` só aparece quando há um
 * item só — aí não existe composição, e a faixa inteira é o único jeito de não
 * deixar vão.
 */
const ROW_MAJOR_FIRST: BentoSpan[] = ["major", "minor"];
const ROW_MAJOR_LAST: BentoSpan[] = ["minor", "major"];
const ROW_EVEN: BentoSpan[] = ["minor", "minor", "minor"];

/** Quantas linhas de destaque seguidas antes de intercalar uma uniforme. */
const MAX_FEATURE_ROWS_IN_A_ROW = 2;

/**
 * Quantas linhas de cada tipo cabem exatamente em `count` itens.
 *
 * Uma linha de destaque consome 2 itens e uma uniforme consome 3, então
 * resolvemos `2a + 3b = count`. Entre as soluções, escolhemos a que fica mais
 * perto de duas linhas de destaque para cada uniforme — é a proporção que dá
 * ritmo sem transformar o bloco numa sequência de faixas iguais.
 *
 * Resolver isso primeiro (em vez de ir consumindo itens linha a linha) é o que
 * garante que a última linha feche exata: percorrendo o ciclo às cegas sobrava
 * um item sozinho, ou o destaque sumia depois da primeira linha.
 */
const planRows = (count: number): { featureRows: number; evenRows: number } | null => {
  let best: { featureRows: number; evenRows: number; score: number } | null = null;

  for (let evenRows = 0; evenRows * 3 <= count; evenRows += 1) {
    const rest = count - evenRows * 3;
    if (rest % 2 !== 0) continue;

    const featureRows = rest / 2;
    if (featureRows < 1) continue;

    const score = Math.abs(featureRows - 2 * evenRows) + (featureRows + evenRows) * 0.01;
    if (best === null || score < best.score) best = { featureRows, evenRows, score };
  }

  return best && { featureRows: best.featureRows, evenRows: best.evenRows };
};

/**
 * Devolve o span de cada item, na ordem. O resultado sempre preenche linhas
 * completas de 3 colunas — nunca deixa buraco.
 */
export const bentoSpans = (count: number): BentoSpan[] => {
  if (count <= 0) return [];
  // Um item só ocupa a faixa INTEIRA: com "major" sobraria um terço vazio.
  if (count === 1) return ["full"];
  if (count === 2) return [...ROW_MAJOR_FIRST];
  if (count === 3) return [...ROW_EVEN];

  const plan = planRows(count);
  if (!plan) return [...ROW_EVEN];

  const spans: BentoSpan[] = [];
  let { featureRows, evenRows } = plan;
  let side = 0;
  let featureRowsSinceEven = 0;

  while (featureRows + evenRows > 0) {
    const useEven =
      evenRows > 0 &&
      (featureRows === 0 || featureRowsSinceEven >= MAX_FEATURE_ROWS_IN_A_ROW);

    if (useEven) {
      spans.push(...ROW_EVEN);
      evenRows -= 1;
      featureRowsSinceEven = 0;
      continue;
    }

    // A cada linha de destaque o lado largo troca — é daí que vem a alternância.
    spans.push(...(side % 2 === 0 ? ROW_MAJOR_FIRST : ROW_MAJOR_LAST));
    featureRows -= 1;
    side += 1;
    featureRowsSinceEven += 1;
  }

  return spans;
};

/** Quantas colunas cada span ocupa na grade de três colunas. */
export const BENTO_SPAN_COLUMNS: Record<BentoSpan, number> = {
  major: 2,
  minor: 1,
  full: 3,
};

/** Nenhum span ocupa mais de uma linha — ver o comentário do topo. */
export const BENTO_SPAN_ROWS: Record<BentoSpan, number> = {
  major: 1,
  minor: 1,
  full: 1,
};

/**
 * Em que coluna o primeiro item da ÚLTIMA linha deve começar, para que uma
 * linha incompleta fique centralizada em vez de encostada à esquerda.
 *
 * O CSS Grid não faz isso sozinho: `justify-content` moveria a grade inteira, e
 * `justify-items` mexe dentro da célula, não na distribuição das células. O
 * jeito é dar um `grid-column-start` explícito ao primeiro item da sobra.
 *
 * Devolve `null` quando não há o que ajustar — linha cheia, ou menos itens do
 * que uma linha (aí a única linha já é a primeira, e centralizá-la mudaria o
 * alinhamento que o Champion escolheu).
 *
 * Com número ÍMPAR de colunas vagas não existe centro exato (7 itens em 4
 * colunas deixa 1 vaga). Nesses casos o arredondamento é para baixo — sobra à
 * direita —, que preserva a leitura da esquerda para a direita.
 */
export const lastRowOffset = ({
  total,
  columns,
}: {
  total: number;
  columns: number;
}): number | null => {
  if (columns <= 1 || total <= columns) return null;

  const remainder = total % columns;
  if (remainder === 0) return null;

  const emptyColumns = columns - remainder;
  // +1 porque `grid-column` é 1-indexed.
  return Math.floor(emptyColumns / 2) + 1;
};

/** O índice do primeiro item da última linha incompleta. */
export const lastRowStartIndex = ({
  total,
  columns,
}: {
  total: number;
  columns: number;
}): number | null => {
  if (lastRowOffset({ total, columns }) === null) return null;
  return total - (total % columns);
};
